import React, { useMemo } from "react";
import { Route, Switch, useLocation, useRouteMatch } from "react-router";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Container from "@material-ui/core/Container";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { endOfTitle } from "../../../constants/constants";
import { AdapterLink } from "../../../components/Adopter";
import { Routes } from "../../../constants/routes";
import Error from "../../../components/Error";
import theme from "../../../themes";
import { urlReplacer } from "../../../utils";
import { SchoolStudentApplications } from "../../application/components/SchoolStudentApplications";
import BillingPage from "../../billing/pages/BillingPage";
import { useAuth } from "../../auth/hooks/useAuth";
import { StudentProfileWidget } from "../components/managedStudent/StudentProfileWidget";
import { UserRole } from "../../user/enums/UserRole";
import { useUserAccount } from "../../user/userAccount/context";

const useStyles = makeStyles(() => ({
  tabs: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabLink: {
    "&:hover": {
      textDecoration: "none",
    },
    "@media (min-width: 520px)": {
      minWidth: "unset",
    },
  },
}));

export const SchoolStudentAccountPage = () => {
  const {
    user,
    profile: { data: { data: profile = null } = {} },
  } = useAuth();

  //admin case
  const { user: schoolUser, profile: accountProfile } = useUserAccount();

  const classes = useStyles();
  const { url: path } = useRouteMatch();
  const { pathname } = useLocation();
  const { studentId }: { studentId: string } = useParams();
  const pieces = pathname.split(`${path}`);
  const value = pieces[pieces.length - 1].replace(/^\/|\/$/g, "");
  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);
  const currentRoute = isAdminOrSupport
    ? "/0/user/:userId/edit/student/:studentId"
    : "/0/student/:studentId";

  const schoolId = isAdminOrSupport
    ? accountProfile?.data.data.school?.id
    : profile.school.id;

  const tabs = useMemo(
    () => [
      {
        label: "Applications",
        component: () => <SchoolStudentApplications studentId={studentId} />,
        path: `/${Routes.APPLICATIONS}`,
      },
      {
        label: "Billing",
        component: () => (
          <BillingPage
            schoolId={schoolId}
            studentId={studentId}
            showFilter={false}
          />
        ),
        path: `/${Routes.BILLING}`,
      },
    ],
    [],
  );

  const getMessagesPageUrl = () => {
    if (isAdminOrSupport) {
      return `/0/user/${schoolUser?.id}/edit/${Routes.MESSAGES}?userRole=${UserRole.Student}&recipientId=${studentId}`;
    }

    return `/0/${urlReplacer(Routes.USER_MESSAGES, {
      userRole: UserRole.Student,
      recipientId: studentId,
    })}`;
  };

  return (
    <Container>
      <Helmet>
        <title>Student{endOfTitle}</title>
      </Helmet>
      <StudentProfileWidget
        id={studentId}
        actionButtonType={"email"}
        onActionButtonClick={getMessagesPageUrl}
        hasUnreadMessage={false}
      />
      <Tabs value={value} variant="scrollable" className={classes.tabs}>
        {tabs.map((item, index) => (
          <Tab
            component={AdapterLink}
            className={classes.tabLink}
            key={index}
            label={item.label}
            value={item.path.replace(/^\/|\/$/g, "")}
            to={path + item.path}
          />
        ))}
      </Tabs>
      <Switch>
        {tabs.map((item, index) => (
          <Route
            key={index}
            exact
            path={currentRoute + item.path}
            component={item.component}
          />
        ))}
        <Route
          path={`${path}/*`}
          component={() => <Error text={"Not found"} />}
        />
      </Switch>
    </Container>
  );
};
